import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ConversionCreditCampaignStatus } from '#src/__generated__/globalTypes.ts';
import { LANDING } from '#src/constants/__generated__/routes.ts';
import { useDefaultSportPages } from '#src/constants/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { monetaryAmountFragment } from '#src/lib/monetaryAmount.ts';

import type {
  CampaignConversionCreditQuery,
  CampaignConversionCreditQueryVariables,
} from './__generated__/usePromoSignup.graphql.ts';
import { useQuery } from './graphql/useQuery.ts';
import { useSessionStorage } from './useSessionStorage.ts';

const CAMPAIGN_CONVERSION_CREDIT_QUERY = gql`
  query CampaignConversionCreditQuery($code: String!) {
    tokens {
      conversionCreditCampaign(code: $code) {
        id
        sport
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
        status
        percentageDiscount
        endDate
        campaignCode
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<
  CampaignConversionCreditQuery,
  CampaignConversionCreditQueryVariables
>;

export const usePromoSignup = (code?: string) => {
  const { currentUser } = useCurrentUserContext();
  const [, setSignupPromo] = useSessionStorage('signup-promo');
  const { showNotification } = useSnackNotificationContext();
  const defaultSportPages = useDefaultSportPages();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { data } = useQuery(CAMPAIGN_CONVERSION_CREDIT_QUERY, {
    variables: { code: code! },
    skip: !code,
  });

  useEffect(() => {
    if (!currentUser && data) {
      const campaign = data.tokens.conversionCreditCampaign;
      if (campaign?.status === ConversionCreditCampaignStatus.ACTIVE) {
        setSignupPromo({
          ...campaign,
          endDate: campaign.endDate.toISOString(),
        });
        showNotification('promoApplied');
      }
      if (campaign?.sport) {
        navigate(`${defaultSportPages[campaign.sport]}${search}`);
      } else {
        navigate(LANDING, { replace: true });
      }
    }
  }, [
    currentUser,
    data,
    defaultSportPages,
    navigate,
    search,
    setSignupPromo,
    showNotification,
  ]);
};
